/* eslint-disable max-lines */
import { useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { FormattedMessage, useIntl } from "react-intl";
import { OverlayTrigger, Tooltip, ButtonGroup } from "react-bootstrap";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import usePermissions from "hooks/usePermissions";
import testIds from "constants/testIds";
import { cancelManualQuote, requestQuotationExtension } from "api";
import { toast } from "react-toastify";
import errorToast from "helpers/errorToast";
import getLabel, { getLabelWithTranslationFallback } from "helpers/getLabel";
import { ReactComponent as CircleCross } from "assets/images/icons/CircleCross.svg";

import Button from "components/atoms/Button/Button";
import Badge from "components/atoms/Badge/Badge";
import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import { formatDate, DateTimeFormat } from "helpers/dateTimeHelper";
import ManageApprovalDialog from "../ManageApprovalDialog";
import TransferApprovalDialog from "../TransferApprovalDialog";
import RequestApprovalDialog from "../RequestApprovalDialog";
import ConfirmBookQuoteDialog from "../ConfirmBookQuoteDialog";
import CancelApprovalRequestDialog from "../CancelApprovalRequestDialog";
import QuoteExtensionDialog from "../QuoteExtensionDialog";
import ManualParcelQuoteDialog from "../ManualParcelQuoteDialog";

function QuoteActionButton({
  approvalPending,
  offPlatformShipment,
  requestStatus,
  id,
  quotesData,
  quotationSettings,
  fetchData,
}) {
  const intl = useIntl();
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const { booking } = JSON.parse(permissions)?.permissions;
  const { general, financial, created_by_user_type, actions, schedule, reference, status, provider } = quotesData;
  const { menu_items } = JSON.parse(permissions);

  const { customs_broker_available } = quotationSettings;
  const [quotationId, setQuotationId] = useState(null);
  const [quoteExtensionDialog, setQuoteExtensionDialog] = useState(false);
  const [showManualQuoteDialog, setShowManualQuoteDialog] = useState(false);
  const [selectedQuoteId, setSelectedQuoteId] = useState(null);
  const [showManageApprovalDialog, setShowManageApprovalDialog] = useState(false);
  const [showTransferApprovalDialog, setShowTransferApprovalDialog] = useState(false);
  const [showCancelApprovalRequestDialog, setShowCancelApprovalRequestDialog] = useState(false);
  const [showRequestApprovalDialog, setShowRequestApprovalDialog] = useState(false);
  const [showBookQuoteDialog, setShowBookQuoteDialog] = useState(false);
  const [bookingType, setBookingType] = useState("");
  const [bookingPayload, setBookingPayload] = useState({});
  const [isBookAsDuplicate, setIsBookAsDuplicate] = useState(false);
  const [isAdditionalApproval, setIsAdditionalApproval] = useState(false);
  const [isSuggestApproval, setIsSuggestApproval] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showCustomsBroker, setShowCustomsBroker] = useState(false);
  const [isRequestingExtension, setIsRequestingExtension] = useState(false);
  const isQuotationExpired = general.quote_expiration_seconds_remaining < 0;

  const editQuoteData = {
    reference,
    tracking_number: general.tracking_number,
    pickup_date: schedule.quoted_pickup_date,
    pickup_time: schedule.quoted_pickup_date,
    delivery_date: schedule.quoted_time_delivery_date,
    delivery_time: schedule.quoted_time_delivery_date,
    value: parseInt(financial.total_quoted_amount_lsp),
    currency_code: financial.lsp_currency,
  };

  const { data: shipmentData } = useGetShipmentBasic(id, {
    enabled: !!id,
  });

  // Get the latest quotation and quote request data
  const fetchQuoteRequestData = () => {
    fetchData();
  };

  const handleEditManualQuote = () => {
    setShowManualQuoteDialog(true);
  };

  const handleCancelManualQuote = (reference) => {
    cancelManualQuote(reference)
      .then((res) => {
        if (res.status === 200) {
          toast("Quotation cancelled", { type: "success" });
          fetchQuoteRequestData();
        } else throw new Error("Could not cancel quote. Please try again later.");
      })
      .catch((err) => {
        errorToast(err, "Could not cancel quote. Please try again later.");
      });
  };

  const handleRequestApprovalClick = (
    quotationId,
    isBookAsDuplicate,
    isAdditionalApproval = false,
    isSuggestApproval = false,
  ) => {
    setShowCustomsBroker(customs_broker_available);
    setSelectedQuoteId(quotationId);
    setIsBookAsDuplicate(isBookAsDuplicate);
    setIsAdditionalApproval(isAdditionalApproval);
    setIsSuggestApproval(isSuggestApproval);
    setShowRequestApprovalDialog(true);
  };

  const handleBookQuotationClick = (quotationId) => {
    setSelectedQuoteId(quotationId);
    setShowCustomsBroker(customs_broker_available);
    setShowBookQuoteDialog(true);
  };

  const handleManageApprovalClick = (quotationId) => {
    setSelectedQuoteId(quotationId);
    setShowCustomsBroker(customs_broker_available);
    setShowManageApprovalDialog(true);
  };

  const handleCancelApprovalRequestClick = (e, quotationId) => {
    e.stopPropagation();
    setSelectedQuoteId(quotationId);
    setShowCancelApprovalRequestDialog(true);
  };

  const handleRequestQuoteExtension = (reference) => {
    setQuoteExtensionDialog(true);
    setQuotationId(reference);
  };

  const openTransferApprovalDialog = () => {
    setShowTransferApprovalDialog(true);
  };

  function closeQuoteExtensionModal() {
    setQuoteExtensionDialog(false);
    setErrorMessage(null);
  }

  function submitQuoteExtension() {
    setIsRequestingExtension(true);
    requestQuotationExtension(quotationId)
      .then(() => {
        closeQuoteExtensionModal();
        fetchQuoteRequestData();
      })
      .catch((error) => {
        errorToast(error);
        fetchData();
        closeQuoteExtensionModal();
      })
      .finally(() => setIsRequestingExtension(false));
  }
  const bookLabel =
    getLabel("Book", "capitalize-first", shipmentData?.organization?.payment_location_reference, true) ??
    quotationSettings.book_label;
  const showManageApprovalButton =
    ((actions.can_approve || actions.can_preapprove) && status === "require_approval") ||
    (actions.can_book && status === "reapproval_needed_after_booking");

  const showRequestApprovalButton =
    (!actions.can_book && (status === "open_to_book" || status === "submitted")) ||
    (!actions.can_book && status === "reapproval_needed_after_booking" && actions.reapproval_needed_after_booking);

  const showActiveActionButtons =
    status === "reapproval_needed_after_booking" ||
    moment(general.quote_expiration, "YYYY-MM-DD HH:mm:ss ZZ").isAfter(moment()) ||
    approvalPending;

  // An in-transit (post booking) cost re-approval request leaves the quote status at
  // "reapproval_needed_after_booking", so the pending request has to be detected from
  // can_cancel_approval_request, which is only true while a request the user raised is open.
  const hasPendingApprovalRequest =
    status === "require_approval" || (status === "reapproval_needed_after_booking" && actions.can_cancel_approval_request);

  return (
    <>
      <CancelApprovalRequestDialog
        isOpen={showCancelApprovalRequestDialog}
        close={() => {
          setShowCancelApprovalRequestDialog(false);
        }}
        quotationId={selectedQuoteId}
        fetchQuoteRequestData={fetchQuoteRequestData}
      />
      <ManageApprovalDialog
        isOpen={showManageApprovalDialog}
        setShowConfirmBookQuoteDialog={setShowBookQuoteDialog}
        close={() => {
          setShowManageApprovalDialog(false);
          setShowBookQuoteDialog(false);
        }}
        openTransferApprovalDialog={openTransferApprovalDialog}
        shipmentId={id}
        quotationId={selectedQuoteId}
        fetchQuoteRequestData={fetchQuoteRequestData}
        quotationSettings={quotationSettings}
        setBookingPayload={setBookingPayload}
        setBookingType={setBookingType}
        setShowBookQuoteDialog={setShowBookQuoteDialog}
        status={status}
        isQuotationExpired={isQuotationExpired}
        isFedexPostmenQuotation={general.is_fedex_postmen_quote}
      />
      <QuoteExtensionDialog
        quoteExtensionDialog={quoteExtensionDialog}
        closeQuoteExtensionModal={closeQuoteExtensionModal}
        submitQuoteExtension={submitQuoteExtension}
        isRequestingExtension={isRequestingExtension}
        errorMessage={errorMessage}
      />

      {selectedQuoteId && (
        <TransferApprovalDialog
          isOpen={showTransferApprovalDialog}
          close={() => setShowTransferApprovalDialog(false)}
          fetchQuoteRequestData={fetchQuoteRequestData}
          setBookingPayload={setBookingPayload}
          openManageApprovalDialog={() => setShowManageApprovalDialog(true)}
          setShowBookQuoteDialog={setShowBookQuoteDialog}
          setBookingType={setBookingType}
          quotationId={selectedQuoteId}
          shipmentId={id}
          quotationSettings={quotationSettings}
          status={status}
        />
      )}

      {showManualQuoteDialog && (
        <ManualParcelQuoteDialog
          isOpen={showManualQuoteDialog}
          close={() => {
            setShowManualQuoteDialog(false);
            setShowBookQuoteDialog(false);
          }}
          editManualQuoteData={editQuoteData}
          fetchQuoteRequestData={fetchQuoteRequestData}
          isParcel={provider?.is_parcel_company}
          isManualQuote={created_by_user_type === "shipper"}
        />
      )}

      {selectedQuoteId && (
        <RequestApprovalDialog
          isOpen={showRequestApprovalDialog}
          close={() => {
            setShowBookQuoteDialog(false);
            setShowRequestApprovalDialog(false);
          }}
          openManageApprovalDialog={() => setShowRequestApprovalDialog(true)}
          quotationId={selectedQuoteId}
          isBookAsDuplicate={isBookAsDuplicate}
          setShowBookQuoteDialog={setShowBookQuoteDialog}
          setBookingPayload={setBookingPayload}
          isAdditionalApproval={isAdditionalApproval}
          isSuggestApproval={isSuggestApproval}
          setBookingType={setBookingType}
          fetchQuoteRequestData={fetchQuoteRequestData}
          shipmentId={id}
          quotationSettings={quotationSettings}
          status={status}
        />
      )}
      {showBookQuoteDialog && (
        <ConfirmBookQuoteDialog
          close={() => {
            setShowCustomsBroker(false);
            setShowBookQuoteDialog(false);
          }}
          quotationId={selectedQuoteId}
          isApprovalRequested={showActiveActionButtons && showManageApprovalButton}
          bookingType={bookingType}
          requestData={bookingPayload}
          isBookAsDuplicate={quotationSettings.book_as_duplicate}
          fetchQuoteRequestData={fetchQuoteRequestData}
          show={showBookQuoteDialog}
          setShowBookQuoteDialog={setShowBookQuoteDialog}
          shipmentId={id}
          showCustomsBroker={showCustomsBroker}
          setShowCustomsBroker={setShowCustomsBroker}
          quotationSettings={quotationSettings}
          customBrokerAvailable={customs_broker_available}
          status={status}
          isFedexPostmenQuotation={general.is_fedex_postmen_quote}
          isFedexDirectIntegration={general.is_fedex_direct_integration}
        />
      )}
      <div>
        {/* Booked information
            If the quote is booked then we only show the booked badge and the booked as duplicate shipment id if it exists, no button is shown along with this
        */}
        {(status === "booked" || status === "booked_as_duplicate" || general.booked_as_duplicate_shipment_id) && (
          <div className="d-flex flex-column align-items-start">
            <Badge
              label={intl.formatMessage({
                id: "general.booked",
                defaultMessage: "Booked",
              })}
              type="primary"
            />
            {(status === "booked_as_duplicate" || general.booked_as_duplicate_shipment_id) && (
              <p>
                {" "}
                <p className="mb-0 font-weight-bold mt-2">
                  <span className="d-flex align-items-center font-b4">
                    <FormattedMessage id="shipment-quotes.components.quotes.bookedOn" defaultMessage="Booked on" />{" "}
                    {formatDate(general.booked_as_duplicate_date, DateTimeFormat.dateFormat)}
                  </span>
                </p>
                <p>
                  {general.booked_as_duplicate_shipment_id && (
                    <Button
                      variant="link"
                      className="pl-0 ml-0 font-b4"
                      style={{ fontSize: 11 }}
                      onClick={() => {
                        navigate(`/shipment/${general.booked_as_duplicate_shipment_id}/tracking`);
                      }}
                    >
                      <span className="font-weight-bold">{general.booked_as_duplicate_shipment_id}</span>
                    </Button>
                  )}
                </p>
              </p>
            )}
          </div>
        )}
        {(requestStatus === "Canceled" || status === "canceled") &&
          status !== "canceled_by_provider" &&
          !general.booked_as_duplicate_shipment_id && (
            <div className="d-flex">
              {!general.is_invalid_freight_mode ? (
                <Badge
                  label={intl.formatMessage({
                    id: "general.canceled",
                    defaultMessage: "Canceled",
                  })}
                  type="secondary"
                />
              ) : (
                <Badge
                  label={intl.formatMessage({
                    id: "shipment-quotes.components.quotes.invalidFreightMode",
                    defaultMessage: "Invalid freight mode",
                  })}
                  type="secondary"
                />
              )}
            </div>
          )}
        {status === "lost" && (
          <div className="d-flex">
            <Badge
              label={intl.formatMessage({
                id: "general.lost",
                defaultMessage: "Lost",
              })}
              type="danger"
            />
          </div>
        )}
        {status === "canceled_by_provider" && (
          <div className="d-flex">
            <Badge
              label={intl.formatMessage({
                id: "shipment-quotes.components.quotes.canceledByProvider",
                defaultMessage: "Canceled by provider",
              })}
              type="secondary"
            />
          </div>
        )}
        {status === "confirmation_needed" && (
          <div className="d-flex">
            <OverlayTrigger
              placement="bottom"
              delay={{ show: 250, hide: 0 }}
              overlay={
                <Tooltip id="button-tooltip">
                  <FormattedMessage
                    id="shipment-quotes.components.quotes.providerConfirmQuote"
                    defaultMessage="Provider has to confirm quote before it can be booked"
                  />
                </Tooltip>
              }
            >
              <div className="mt-3 mb-2 border-0 border-solid br-3 pb-1 pl-2 pr-2 pt-1 font-weight-bold bg-listing-status-passive listing-status-passive">
                <FormattedMessage
                  id="shipment-quotes.components.quotes.confirmationRequested"
                  defaultMessage="Confirmation Requested"
                />
              </div>
            </OverlayTrigger>
          </div>
        )}
        {/* Dislay action button or Booked information if already booked
            Only shown if it's not an offplatform shipment and the quote is not canceled or requiring confirmation
         */}
        {!offPlatformShipment &&
          requestStatus !== "Canceled" &&
          requestStatus !== "Auto canceled" &&
          status !== "confirmation_needed" &&
          status !== "canceled" &&
          status !== "canceled_by_provider" && (
            <div>
              <div className="d-flex flex-column align-items-start">
                {/* Action buttons
                    Anything after here renders an action button and it's relevant text/tooltip
                 */}
                {status !== "booked" && status !== "booked_as_duplicate" && status !== "lost" && (
                  <>
                    {/* Quote active action buttons
                        These buttons are shown if the quote is not expired, after this block you can find the expired quote elements
                     */}
                    {showActiveActionButtons && (
                      <>
                        {/* Book quote button
                            Shown if:
                            - can_book: true -> The user is allowed to directly book a quote
                            - booking.can_request_book_shipments: true -> The user has the global permission to request/book shipments
                            - can_approve: false -> The user is not allowed to approve the quote, this is only ever true if the quote requires approval and the user is allowed to approve it
                            - !showManageApprovalButton -> manage approval button is not shown
                         */}
                        {actions.can_book &&
                          booking?.can_request_book_shipments &&
                          !actions.can_approve &&
                          !showManageApprovalButton &&
                          status !== "reapproval_needed_after_booking" && (
                            <div className="mb-2 no-wrap">
                              <Button
                                variant="primary"
                                onClick={() => handleBookQuotationClick(reference)}
                                data-testid={testIds.SHIPMENT_DASHBOARD_BOOK_QUOTE_BUTTON}
                                disabled={isQuotationExpired}
                              >
                                {bookLabel}{" "}
                                {quotationSettings.book_as_duplicate && (
                                  <OverlayTrigger
                                    placement="bottom"
                                    delay={{ show: 250, hide: 0 }}
                                    overlay={
                                      <Tooltip id="button-tooltip">
                                        <FormattedMessage
                                          id="shipment-quotes.components.quotes.tooltip"
                                          defaultMessage="Booking this quote will duplicate the request and the quote. A new shipment number will be created as a separate reference for the booked shipment"
                                        />
                                      </Tooltip>
                                    }
                                  >
                                    <FontAwesomeIcon className="ml-2" icon={faQuestionCircle} />
                                  </OverlayTrigger>
                                )}
                              </Button>
                              {/* Suggest approval button
                                Shown if:
                                  - can_book: true -> The user is allowed to directly book a quote
                                  - can_suggest_approve: true -> The user is allowed to suggest approval
                                  - approvalPending: false -> The quote does not require approval
                             */}
                              {quotationSettings.can_suggest_approve && actions?.can_suggest_approval && !approvalPending && (
                                <Button
                                  onClick={() =>
                                    handleRequestApprovalClick(reference, quotationSettings.book_as_duplicate, false, true)
                                  }
                                  label={intl.formatMessage({
                                    id: "shipment-quotes.components.quotes.suggestApproval",
                                    defaultMessage: "Suggest Approval",
                                  })}
                                  variant="link"
                                  className="p-0 mt-2 mb-0"
                                />
                              )}
                            </div>
                          )}
                        {/* Manage approval button
                            Shown if:
                            - can_approve: true -> The user is allowed to approve the quote
                            - can_preapprove: true -> The user is allowed to preapprove the quote
                            - status: require_approval -> The quote currently has an approval request pending
                            or:
                            - status: reapproval_needed_after_booking -> The quote needs to be re-approved after booking
                            - can_book: true -> The user is allowed to book the quote
                         */}
                        {showManageApprovalButton && (
                          <div className="mb-2 no-wrap">
                            <p className="font-b3 text-warning mb-2">
                              {status === "reapproval_needed_after_booking" ? (
                                <FormattedMessage
                                  id="shipment-quotes.components.quotes.requiredRe-approval"
                                  defaultMessage="You are requested to re-approve this quote"
                                />
                              ) : (
                                <>
                                  {!isQuotationExpired && (
                                    <FormattedMessage
                                      id="shipment-quotes.components.quotes.requiredApproval"
                                      defaultMessage="You are requested to approve this quote"
                                    />
                                  )}
                                </>
                              )}
                            </p>
                            <Button
                              label={intl.formatMessage({
                                id: "shipment-quotes.components.quotes.manageApproval",
                                defaultMessage: "Manage Approval",
                              })}
                              variant="success"
                              className="bg-success border-0"
                              onClick={() => handleManageApprovalClick(reference)}
                            />
                          </div>
                        )}
                        {/* Approval requested button
                            Shown if:
                            - can_approve: false -> The user is not allowed to approve the quote i.e they're not one of the requested approvers
                            - can_preapprove: false -> The user is not allowed to preapprove the quote
                            - status: require_approval -> The quote currently has an approval request pending
                            - can_book: false -> The user is not allowed to directly book a quote, otherwise they would see a book quote button if this one was true and can_approve was false
                            - can_book: true and can_cancel_approval_request: true -> The user had permission to directly book the quote, but it chose 'suggest approval' to go through the approval process.
                        */}
                        {!actions.can_approve &&
                          !actions.can_preapprove &&
                          hasPendingApprovalRequest &&
                          (!actions.can_book || (actions.can_book && actions.can_cancel_approval_request)) && (
                            <div className="mb-2 no-wrap">
                              <ButtonGroup>
                                <Button
                                  label={intl.formatMessage({
                                    id: "shipment-quotes.components.quotes.approvalRequested",
                                    defaultMessage: "Approval requested",
                                  })}
                                  variant="secondary"
                                  disabled
                                />
                                {/* Cancel approval request button
                                    Shown if:
                                      - can_cancel_approval_request: true -> The user is allowed to cancel the approval request, they are the creator of the approval request
                                 */}
                                {actions.can_cancel_approval_request && (
                                  <div className="d-flex align-items-center">
                                    <OverlayTrigger
                                      placement="top"
                                      delay={{ show: 250, hide: 0 }}
                                      overlay={
                                        <Tooltip id="button-tooltip">
                                          <FormattedMessage id="general.cancel" defaultMessage="Cancel" />
                                        </Tooltip>
                                      }
                                    >
                                      <CircleCross
                                        onClick={(e) => handleCancelApprovalRequestClick(e, reference)}
                                        className="ml-2"
                                      />
                                    </OverlayTrigger>
                                  </div>
                                )}
                              </ButtonGroup>
                              {status === "reapproval_needed_after_booking" && (
                                <div>
                                  <div className="text-secondary font-italic font-b3 mt-2">
                                    <FormattedMessage
                                      id="shipment-quotes.components.quotes.reapprovalForAdditionalCosts"
                                      defaultMessage="Re-approval for additional costs is requested."
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        {/* Request approval button
                              Shown if:
                                - can_book: false -> The user is not allowed to directly book a quote
                                - status: "open_to_book" OR "submitted"
                                - quotes_threshold_exceeded: true -> The number of quotes on the request must exceed this limit before they are allowed to book/request
                              OR:
                                - can_book: false -> The user is not allowed to directly book a quote
                                - status: reapproval_needed_after_booking -> The quote needs to be re-approved after booking
                                - action.reapproval_needed_after_booking: true
                                - quotes_threshold_exceeded: true -> The number of quotes on the request must exceed this limit before they are allowed to book/request
                              Otherwise, show a disabled button with a tooltip explaining why the user cannot book the quote
                           */}
                        {booking?.can_request_book_shipments && (
                          <>
                            {showRequestApprovalButton && (
                              <div className="mb-2 no-wrap">
                                {quotationSettings.quotes_threshold_exceeded ? (
                                  <Button
                                    label={
                                      getLabelWithTranslationFallback("Request approval", {
                                        translation: (
                                          <FormattedMessage
                                            id="shipment-quotes.components.quotes.requestApproval"
                                            defaultMessage="Request approval"
                                          />
                                        ),
                                        style: "capitalize-first",
                                        paymentLocation: shipmentData?.organization?.payment_location_reference,
                                        returnAsString: true,
                                      }) ?? quotationSettings.request_approve_label
                                    }
                                    variant="outline-primary"
                                    disabled={
                                      !actions.can_select ||
                                      approvalPending ||
                                      (!actions.reapproval_needed_after_booking &&
                                        status === "reapproval_needed_after_booking")
                                    }
                                    data-testid={testIds.SHIPMENT_DASHBOARD_REQUEST_APPROVAL_BUTTON}
                                    onClick={() =>
                                      handleRequestApprovalClick(reference, quotationSettings.book_as_duplicate)
                                    }
                                  />
                                ) : (
                                  <OverlayTrigger
                                    placement="top"
                                    delay={{ show: 250, hide: 0 }}
                                    overlay={
                                      <Tooltip id="button-tooltip">
                                        <FormattedMessage
                                          id="shipment-quotes.components.quotes.bookingAvailableAfterDeadline"
                                          defaultMessage="Booking will be available after the quoting deadline has passed"
                                        />
                                      </Tooltip>
                                    }
                                  >
                                    <span>
                                      <Button
                                        label={bookLabel}
                                        variant="outline-primary"
                                        disabled
                                        style={{ pointerEvents: "none" }}
                                        data-testid={testIds.SHIPMENT_DASHBOARD_REQUEST_APPROVAL_BUTTON}
                                        onClick={() =>
                                          handleRequestApprovalClick(reference, quotationSettings.book_as_duplicate)
                                        }
                                      />
                                    </span>
                                  </OverlayTrigger>
                                )}
                              </div>
                            )}
                          </>
                        )}
                        {/* Edit and cancel buttons for manual quotes created by the user */}
                        {quotesData?.created_on_newui &&
                          created_by_user_type === "shipper" &&
                          status !== "canceled" &&
                          status !== "Auto canceled" && (
                            <div className="d-flex">
                              <Button
                                label={intl.formatMessage({
                                  id: "general.edit",
                                  defaultMessage: "Edit",
                                })}
                                variant="outline-primary"
                                onClick={handleEditManualQuote}
                              />
                              <Button
                                label={intl.formatMessage({
                                  id: "general.cancel",
                                  defaultMessage: "Cancel",
                                })}
                                variant="danger"
                                className="ml-2"
                                onClick={() => handleCancelManualQuote(reference)}
                              />
                            </div>
                          )}
                        {/* Add additional approvers
                            If the user is allowed to add additional approvers and the user is not allowed to approve the quote then show the add additional approvers button
                         */}
                        {actions.can_add_additional_approvers && !actions.can_approve && hasPendingApprovalRequest && (
                          <Button
                            onClick={() => handleRequestApprovalClick(reference, quotationSettings.book_as_duplicate, true)}
                            label={intl.formatMessage({
                              id: "shipment-quotes.components.quotes.addAdditionalApprovers",
                              defaultMessage: "Add additional approvers",
                            })}
                            variant="link"
                            className="p-0 mt-2 mb-2"
                          />
                        )}
                        {/* Quote time until expiration
                            Show the quote expiration time if the quote is not expired and the quote is open to book
                          */}
                        {booking.can_request_book_shipments && (
                          <>
                            {status !== "canceled" && status !== "Auto canceled" && status !== "confirmation_needed" && (
                              <OverlayTrigger
                                placement="bottom"
                                delay={{ show: 250, hide: 0 }}
                                overlay={
                                  <Tooltip id="button-tooltip">
                                    {`${moment(general.quote_expiration, "YYYY-MM-DD HH:mm:ss ZZ").format(
                                      "DD MMM YYYY, h:mm a",
                                    )}`}
                                  </Tooltip>
                                }
                              >
                                <span
                                  className={`mt-2 font-b3 cursor-pointer ${
                                    general.quote_expiration_seconds_remaining < 86400 && "text-warning"
                                  } `}
                                >
                                  {general.quote_expiration_remaining_time_display ? (
                                    <FormattedMessage
                                      id="shipment-quotes.components.quotes.quoteExpires"
                                      defaultMessage="Quote expires in {quote_expiration}"
                                      values={{
                                        quote_expiration: `${general.quote_expiration_remaining_time_display}`,
                                      }}
                                    />
                                  ) : (
                                    <FormattedMessage id="quote.quoteExpired" defaultMessage="Quote expired" />
                                  )}
                                </span>
                              </OverlayTrigger>
                            )}
                          </>
                        )}
                      </>
                    )}
                    {/* Canceled after booking */}
                    {status === "canceled_after_booking" && (
                      <div className="mb-2 no-wrap">
                        <span className="border-0 border-solid br-3 pb-1 pl-2 pr-2 pt-1 font-weight-bold listing-status-danger bg-listing-status-danger">
                          <FormattedMessage
                            id="shipment-quotes.components.quotes.canceledAfterBooking"
                            defaultMessage="Canceled after booking"
                          />
                        </span>
                      </div>
                    )}
                    {/* Quote has expired message */}
                    {isQuotationExpired &&
                      status !== "reapproval_needed_after_booking" &&
                      status !== "canceled" &&
                      status !== "Auto canceled" &&
                      status !== "canceled_after_booking" && (
                        <>
                          <div className="mb-2 no-wrap">
                            {approvalPending ? (
                              <span className="mt-2 font-b3 text-warning">
                                <FormattedMessage
                                  id="shipment-quotes.components.quotes.quotationHasExpired"
                                  defaultMessage="Quotation has expired"
                                />
                              </span>
                            ) : (
                              <Button
                                label={intl.formatMessage({
                                  id: "shipment-quotes.components.quotes.quotationHasExpired",
                                  defaultMessage: "Quotation has expired",
                                })}
                                variant="secondary"
                                disabled
                              />
                            )}
                          </div>
                          <span>
                            {/* Extension requesting */}
                            {general.extension_requested_at && general.extension_requested_by ? (
                              <OverlayTrigger
                                placement="bottom"
                                delay={{ show: 250, hide: 0 }}
                                overlay={
                                  <Tooltip>
                                    {general.extension_requested_by} - {moment(general.extension_requested_at).fromNow()}
                                  </Tooltip>
                                }
                              >
                                <div className="text-secondary font-b3">
                                  <FormattedMessage
                                    id="shipment-quotes.components.quotes.extensionRequested"
                                    defaultMessage="Extension requested"
                                  />
                                </div>
                              </OverlayTrigger>
                            ) : (
                              menu_items.create_quote_requests &&
                              booking.can_request_book_shipments && (
                                <Button
                                  label={intl.formatMessage({
                                    id: "shipment-quotes.components.quotes.requestQuoteExtension",
                                    defaultMessage: "Request quote extension",
                                  })}
                                  variant="link"
                                  className="pl-0"
                                  style={{ fontSize: "11px" }}
                                  onClick={() => {
                                    handleRequestQuoteExtension(reference);
                                  }}
                                />
                              )
                            )}
                          </span>
                        </>
                      )}
                  </>
                )}
              </div>
            </div>
          )}
      </div>
    </>
  );
}

QuoteActionButton.propTypes = {
  approvalPending: PropTypes.bool.isRequired,
  id: PropTypes.string.isRequired,
  quotesData: PropTypes.instanceOf(Object).isRequired,
  offPlatformShipment: PropTypes.bool,
  quotationSettings: PropTypes.instanceOf(Object).isRequired,
  requestStatus: PropTypes.string.isRequired,
  fetchData: PropTypes.func.isRequired,
};

QuoteActionButton.defaultProps = {
  offPlatformShipment: false,
};

export default QuoteActionButton;
